/**
 * Defines types and methods to compute behavior attributes.
 *
 * Ported from [behavior_attributes@ab9709a52d](https://github.com/optimizely/optimizely/blob/ab9709a52d9b2579c72d436556b1d2c3c4276a6c/src/www/js/client/behavior_attributes.js)
 *
 * @module
 */

var Logger = require('core/utils/logger');

// TODO(tyler): bad naming
var behavior:any = {
  FIELDS: require('core/behavior/enums').FIELDS,
};
var ruleBuilder = require('./rule_builder');
var rules = require('./rules');

/**
 * Defines types and methods to compute behavior attributes.
 */

/**
 * Granularity of events to evaluate over.
 * @enum {string}
 */
exports.GRANULARITY = {
  ALL: 'all',
  CURRENT_SESSION: 'current_session',
  LAST_30_DAYS: 'last_30_days',
  LAST_60_DAYS: 'last_60_days'
};

/**
 * An attribute descriptor: id, input granularity, a behavior rule and a
 * default value to use.
 * @typedef {Object} BehaviorAttributeDescriptor
 * @property {string} id
 * @property {behavior_attributes.GRANULARITY} granularity
 * @property {Object} rule
 * @property {*} defaultValue
 */

/**
 * Returns events from the current session.
 * @param {Array.<module:core/behavior/event~Event>} events
 * @returns {Array.<module:core/behavior/event~Event>} Events from current session.
 * @private
 */
function extractCurrentSession(events:import('core/behavior/event').Event[]): import('core/behavior/event').Event[] {
  if (events.length === 0) {
    return [];
  }
  var i = events.length - 1;
  var sessionIdField = behavior.FIELDS.SESSION_ID;
  var currentSessionId = events[i][sessionIdField];
  while (i > 0 && currentSessionId === events[i - 1][sessionIdField]) {
    i--;
  }

  return events.slice(i);
}

/**
 * Returns events of the last d days.
 * @param {Array.<module:core/behavior/event~Event>} events
 * @param {number} d number of days.
 * @returns {Array.<module:core/behavior/event~Event>} Events from last d days.
 * @private
 */
function extractLastDDaysEvents(events:import('core/behavior/event').Event[], d:number): import('core/behavior/event').Event[] {
  if (events.length === 0 || d <= 0) {
    return [];
  }
  var lastDDaysThresholdInMillis = (+new Date()) - d * ruleBuilder.MILLIS_IN_A_DAY;
  // Clamp down to midnight.
  lastDDaysThresholdInMillis = lastDDaysThresholdInMillis - (lastDDaysThresholdInMillis % ruleBuilder.MILLIS_IN_A_DAY);
  var i = events.length;
  while(i > 0 && lastDDaysThresholdInMillis <= events[i - 1][behavior.FIELDS.TIME]) {
    i--;
  }

  return events.slice(i);
}

/**
 * Evaluates behavior attributes on events.
 * @param {Array.<module:plugins/feature_behavior/helpers/behavior_attributes~BehaviorAttributeDescriptor>} attributes List of attribute descriptions.
 * @param {Array.<module:core/behavior/event~Event>} events Events to evaluate.
 * @returns {Object.<*>} Results for each evaluated attribute.
 */
exports.evaluate = function(attributes:import('plugins/feature_behavior/helpers/behavior_attributes').BehaviorAttributeDescriptor[], events:import('core/behavior/event').Event[]): Record<string, any> {
  var attributeValues = {} as any;
  if (events.length === 0) {
    for (var i = 0; i < attributes.length; i++) {
      attributeValues[attributes[i].id] = attributes[i].defaultValue;
    }
    return attributeValues;
  }

  var currentSession = extractCurrentSession(events);
  var last60DaysEvents = extractLastDDaysEvents(events, 60);
  for (i = 0; i < attributes.length; i++) {
    var attribute = attributes[i];
    var inputEvents = events;
    if (attribute.granularity === exports.GRANULARITY.CURRENT_SESSION) {
      inputEvents = currentSession;
    } else if (attribute.granularity === exports.GRANULARITY.LAST_60_DAYS) {
      inputEvents = last60DaysEvents;
    }
    try {
      var resultEvents = inputEvents;
      if (attribute.rule) {
        resultEvents = rules.execute(attribute.rule, inputEvents);
      }
      attributeValues[attribute.id] = attribute.defaultValue;
      if (resultEvents.length === 1) {
        attributeValues[attribute.id] = (resultEvents[0][0] || attribute.defaultValue);
      } else {
        Logger.debug('Behavior / Rule for', attribute.id, 'returned',
          resultEvents.length, 'results, expected 1');
      }
    } catch(e) {
      Logger.error('Behavior / Rule for', attribute.id, 'failed with', (e.message || ''));
    }
  }
  return attributeValues;
};

if (__TEST__) {
  exports.extractCurrentSession = extractCurrentSession;
  exports.extractLastDDaysEvents = extractLastDDaysEvents;
}
